import React, { useState, useRef } from "react";
import { Box, Typography, Grid } from "@mui/material";

const DEFAULT_STATUS_LIST = [
  { key: "Associated", color: "#D9C377", altKeys: ["associated"] },
  { key: "Assigned", color: "#7C5480", altKeys: ["assigned"] },
  { key: "Interview", color: "#9C635B", altKeys: ["interview", "interviewed"] },
  { key: "Pending", color: "#9C9292", altKeys: ["pending"] },
  { key: "Submitted", color: "#94BF86", altKeys: ["submitted"] },
  { key: "Selected", color: "#D0DB59", altKeys: ["selected"] },
  { key: "Rejected", color: "#E36854", altKeys: ["rejected"] },
  { key: "Consent Await", color: "#84A3BE", altKeys: ["consentAwait", "consent_await", "consentAwaiting"] },
];

const DEFAULT_COLORS = [
  "#D9C377",
  "#7C5480",
  "#9C635B",
  "#9C9292",
  "#94BF86",
  "#D0DB59",
  "#E36854",
  "#84A3BE",
  "#5B8FF9",
  "#5AD8A6",
  "#F6BD16",
  "#E8684A",
];

const LEGEND_ORDER = [
  "Associated",
  "Assigned",
  "Interview",
  "Pending",
  "Submitted",
  "Selected",
  "Rejected",
  "Consent Await",
];

const CandidateStatusChart = ({ applicantStatusCounts }) => {
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState({
    show: false,
    status: "",
    count: 0,
    color: "",
    x: 0,
    y: 0,
  });

  const parseCounts = () => {
    const counts = applicantStatusCounts || {};
    const chartItems = [];

    if (typeof counts === "object" && counts !== null) {
      const entries = Array.isArray(counts)
        ? counts.map((item) => [
            item.status || item.statusName || item.label || item.name,
            item.count ?? item.value ?? 0,
          ])
        : Object.entries(counts);

      entries.forEach(([key, val]) => {
        if (!key) return;
        const normalizedKey = key.toString().trim();

        // Exclude 'total' (case-insensitive)
        if (normalizedKey.toLowerCase() === "total") return;

        const count = typeof val === "object" && val !== null ? Number(val.count ?? val.value ?? 0) : Number(val || 0);

        let statusName = normalizedKey;
        let color = null;

        // Match with DEFAULT_STATUS_LIST for standardized key name and color
        const matchedConfig = DEFAULT_STATUS_LIST.find(
          (def) =>
            def.key.toLowerCase() === normalizedKey.toLowerCase() ||
            def.altKeys.some((alt) => alt.toLowerCase() === normalizedKey.toLowerCase())
        );

        if (matchedConfig) {
          statusName = matchedConfig.key;
          color = matchedConfig.color;
        }

        if (!color) {
          color = DEFAULT_COLORS[chartItems.length % DEFAULT_COLORS.length];
        }

        chartItems.push({
          status: statusName,
          count: isNaN(count) ? 0 : count,
          color: color,
        });
      });
    }

    return chartItems;
  };

  const chartData = parseCounts();

  // Handle case where no status counts exist
  if (!chartData || chartData.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200, color: "#9ca3af", fontSize: "14px" }}>
        No applicants available for this job
      </Box>
    );
  }

  // Calculate max scale for X Axis
  const maxVal = Math.max(0, ...chartData.map((d) => d.count));
  let maxScale = 10;
  if (maxVal > 100) {
    maxScale = Math.ceil(maxVal / 20) * 20;
  } else if (maxVal > 50) {
    maxScale = 100;
  } else if (maxVal > 20) {
    maxScale = 50;
  } else if (maxVal > 10) {
    maxScale = 20;
  } else if (maxVal > 5) {
    maxScale = 10;
  } else if (maxVal > 0) {
    maxScale = Math.max(5, maxVal);
  }

  // Ticks generation
  const ticks = [];
  const numTicks = maxScale <= 5 ? 5 : 10;
  const step = maxScale / numTicks;
  for (let i = 0; i <= numTicks; i++) {
    const val = Math.round(i * step * 10) / 10;
    ticks.push(Number.isInteger(val) ? val : val.toFixed(1));
  }

  // Chart layout calculations
  const numItems = chartData.length;
  const paddingTop = 15;
  const paddingLeft = 5;
  const paddingRight = 20;
  const plotWidth = 400 - paddingLeft - paddingRight;

  let barHeight = 22;
  let gap = 10;

  if (numItems > 10) {
    barHeight = 18;
    gap = 6;
  } else if (numItems > 7) {
    barHeight = 18;
    gap = 7;
  } else if (numItems > 4) {
    barHeight = 20;
    gap = 8;
  }

  const totalBarsHeight = numItems > 0 ? numItems * barHeight + (numItems - 1) * gap : 0;
  const minPlotAreaHeight = 250;
  const plotAreaHeight = Math.max(minPlotAreaHeight, totalBarsHeight + 20);
  const offsetY = paddingTop + Math.max(0, (plotAreaHeight - totalBarsHeight) / 2);
  const xAxisY = paddingTop + plotAreaHeight;
  const svgHeight = xAxisY + 28;

  // Handle Tooltip
  const handleMouseEnter = (e, item) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setTooltip({
      show: true,
      status: item.status,
      count: item.count,
      color: item.color,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setTooltip((prev) => ({
      ...prev,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    }));
  };

  const handleMouseLeave = () => {
    setTooltip((prev) => ({ ...prev, show: false }));
  };

  // Sort legend items according to LEGEND_ORDER
  const legendItems = [...chartData].sort((a, b) => {
    const idxA = LEGEND_ORDER.indexOf(a.status);
    const idxB = LEGEND_ORDER.indexOf(b.status);
    if (idxA !== -1 && idxB !== -1) return idxA - idxB;
    if (idxA !== -1) return -1;
    if (idxB !== -1) return 1;
    return a.status.localeCompare(b.status);
  });

  return (
    <Box ref={containerRef} sx={{ position: "relative", width: "100%" }}>
      {/* Floating Tooltip */}
      {tooltip.show && (
        <Box
          sx={{
            position: "absolute",
            left: Math.min(tooltip.x + 12, (containerRef.current?.offsetWidth || 300) - 120),
            top: Math.max(10, tooltip.y - 35),
            backgroundColor: "#ffffff",
            color: "#1f2937",
            px: 1.5,
            py: 0.75,
            borderRadius: "8px",
            boxShadow: "0 4px 14px rgba(0, 0, 0, 0.15)",
            border: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            gap: 1,
            zIndex: 20,
            pointerEvents: "none",
            fontSize: "13px",
            fontWeight: 500,
            whiteSpace: "nowrap",
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: tooltip.color,
            }}
          />
          <span>{tooltip.status}</span>
          <Typography component="span" sx={{ fontWeight: 600, fontSize: "13px", color: "#374151" }}>
            ({tooltip.count})
          </Typography>
        </Box>
      )}

      {/* SVG Chart */}
      <Box sx={{ width: "100%", overflow: "visible" }}>
        <svg
          viewBox={`0 0 400 ${svgHeight}`}
          style={{ width: "100%", height: "auto", display: "block", overflow: "visible" }}
        >
          {/* Vertical Grid Ticks & Labels */}
          {ticks.map((t, idx) => {
            const x = paddingLeft + (t / maxScale) * plotWidth;
            return (
              <text key={idx} x={x} y={xAxisY + 15} fontSize="10" fill="#6B7280" textAnchor="middle">
                {t}
              </text>
            );
          })}

          {/* Y Axis Line */}
          <line
            x1={paddingLeft}
            y1={paddingTop}
            x2={paddingLeft}
            y2={xAxisY}
            stroke="#9CA3AF"
            strokeWidth="1.5"
          />

          {/* X Axis Line */}
          <line
            x1={paddingLeft}
            y1={xAxisY}
            x2={paddingLeft + plotWidth}
            y2={xAxisY}
            stroke="#9CA3AF"
            strokeWidth="1.5"
          />

          {/* Horizontal Bars & Count Values */}
          {chartData.map((item, idx) => {
            const y = offsetY + idx * (barHeight + gap);
            const barWidth = maxScale > 0 ? (item.count / maxScale) * plotWidth : 0;
            return (
              <g key={idx}>
                <rect
                  x={paddingLeft}
                  y={y}
                  width={Math.max(0, barWidth)}
                  height={barHeight}
                  fill={item.color}
                  style={{ cursor: "pointer", transition: "opacity 0.2s" }}
                  onMouseEnter={(e) => handleMouseEnter(e, item)}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                />
                <text
                  x={paddingLeft + barWidth + 6}
                  y={y + barHeight / 2}
                  fontSize="11"
                  fontWeight="600"
                  fill="#374151"
                  dominantBaseline="central"
                >
                  ({item.count})
                </text>
              </g>
            );
          })}
        </svg>
      </Box>

      {/* Legend */}
      <Grid container spacing={1} sx={{ mt: 2 }}>
        {legendItems.map((item, idx) => (
          <Grid item xs={4} key={idx}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  backgroundColor: item.color,
                  borderRadius: "3px",
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#374151",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={item.status}
              >
                {item.status}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default CandidateStatusChart;
